import type { TimelineCallItemProps } from '../../../types/timeline/compact-timeline.types';
import { SettingName } from 'src/api/generated';
import { useCallRecording } from 'hooks/api/recordings';
import { useFeatureFlagActive } from 'hooks/settings/feature-flags';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import ButtonModal from 'src/modules/shared/components/molecules/modals/button-modal';
import Loading from 'src/modules/shared/components/atoms/loading';
import { Text } from 'src/modules/shared/components/atoms/text';
import FeedbackModalContent from 'src/components/molecules/analysis/feedback-modal-content/feedback-modal-content';
import SummaryModalContent from 'src/components/molecules/analysis/summary-modal-content/summary-modal-content';
import TranscriptionModalContent from 'src/components/molecules/analysis/transcription-modal-content/transcription-modal-content';
import { hasCallRecording } from '../../../utils/timeline/call-recording.util';
import { TimelineRecordingPlayer } from './timeline-recording-player';

/** Recording controls stay mounted; saved analysis is fetched only when requested. */
export function TimelineCallDetails({
  item,
  activeCallId,
  onPlayRecording,
}: Pick<TimelineCallItemProps, 'item' | 'activeCallId' | 'onPlayRecording'>) {
  const callId = Number(item.id);
  const { recording, loading, error, refetch } = useCallRecording(callId);
  const showRecordings = useFeatureFlagActive(
    SettingName.FEATURE_FLAG_SHOW_RECORDINGS
  );
  const hasRecording = hasCallRecording({ item, recording, error });
  return (
    <div className="space-y-2 border-t border-call-100 mt-2 pt-2">
      {loading ? (
        <Loading className="h-10 items-center" />
      ) : showRecordings && hasRecording && recording?.url ? (
        <TimelineRecordingPlayer
          key={recording.url}
          url={recording.url}
          playing={activeCallId === callId}
          onPlay={() => onPlayRecording?.(callId)}
          onRetry={() => refetch()}
        />
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <Text size="tiny" color="secondary">
            {error && !('status' in error && error.status === 404)
              ? 'No se pudo cargar el audio.'
              : !hasRecording
                ? 'Sin grabación disponible'
                : 'Grabación no disponible para reproducción.'}
          </Text>
          {!!error && !('status' in error && error.status === 404) && (
            <Button
              size="xs"
              appearance="ghost"
              variant="neutral"
              onClick={() => void refetch()}
            >
              Reintentar audio
            </Button>
          )}
        </div>
      )}
      {hasRecording && (
        <div className="flex flex-wrap items-center gap-1">
          <ButtonModal
            buttonContent="Transcripción"
            title="Transcripción"
            size="xs"
            variant="neutral"
            appearance="ghost"
            isIconOnly={false}
          >
            {(enabled) => (
              <TranscriptionModalContent callId={callId} enabled={enabled} />
            )}
          </ButtonModal>
          <ButtonModal
            buttonContent="Resumen"
            title="Resumen"
            size="xs"
            variant="neutral"
            appearance="ghost"
            isIconOnly={false}
            bodyClassName="!p-0"
          >
            {(enabled) => (
              <SummaryModalContent callId={callId} enabled={enabled} />
            )}
          </ButtonModal>
          <ButtonModal
            buttonContent="Feedback"
            title="Feedback"
            size="xs"
            variant="neutral"
            appearance="ghost"
            isIconOnly={false}
            bodyClassName="!p-0"
          >
            {(enabled) => (
              <FeedbackModalContent callId={callId} enabled={enabled} />
            )}
          </ButtonModal>
        </div>
      )}
    </div>
  );
}
